/**
 * API 에러 처리 및 파싱 유틸리티
 * Backend의 일관된 에러 응답 형식 처리
 */

import { AxiosError } from 'axios';
import { logger } from '@/lib/utils/logger';

/**
 * Backend 에러 응답 형식
 * {
 *   "status_code": 400,
 *   "message": "Validation failed.",
 *   "error_code": "validation_error",
 *   "details": { "name": ["필드 에러"] }
 * }
 */
export interface ApiErrorResponse {
  status_code: number;
  message: string;
  error_code: string;
  details?: Record<string, string[]>;
}

/**
 * API 에러를 파싱하는 클래스
 */
export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly errorCode: string;
  public readonly details?: Record<string, string[]>;
  public readonly isNetworkError: boolean;
  public readonly isValidationError: boolean;
  public readonly isAuthError: boolean;
  public readonly isNotFoundError: boolean;

  constructor(
    message: string,
    statusCode: number,
    errorCode: string = 'unknown_error',
    details?: Record<string, string[]>,
    isNetworkError: boolean = false
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.errorCode = errorCode;
    this.details = details;
    this.isNetworkError = isNetworkError;
    this.isValidationError = errorCode === 'validation_error' || statusCode === 400;
    this.isAuthError = statusCode === 401 || statusCode === 403;
    this.isNotFoundError = statusCode === 404;
  }

  /**
   * 사용자 친화적 메시지 반환
   */
  public getUserMessage(): string {
    if (this.isNetworkError) {
      return '네트워크 연결을 확인해주세요.';
    }
    if (this.isAuthError) {
      return '인증이 필요합니다. 다시 로그인해주세요.';
    }
    if (this.isNotFoundError) {
      return '요청한 리소스를 찾을 수 없습니다.';
    }
    if (this.isValidationError) {
      return '입력값이 올바르지 않습니다.';
    }
    return this.message || '오류가 발생했습니다. 나중에 다시 시도해주세요.';
  }

  /**
   * 필드 에러 메시지 반환
   */
  public getFieldError(fieldName: string): string | undefined {
    return this.details?.[fieldName]?.[0];
  }

  /**
   * 모든 필드 에러를 객체로 반환
   */
  public getFieldErrors(): Record<string, string> {
    const errors: Record<string, string> = {};
    if (this.details) {
      Object.entries(this.details).forEach(([field, messages]) => {
        errors[field] = messages[0] || '오류가 발생했습니다';
      });
    }
    return errors;
  }
}

/**
 * Axios 에러를 ApiError로 변환
 */
export function parseAxiosError(error: unknown): ApiError {
  if (error instanceof AxiosError) {
    // 서버 에러 응답이 있는 경우
    if (error.response?.data) {
      const data = error.response.data as ApiErrorResponse;
      return new ApiError(
        data.message || '요청 처리 중 오류가 발생했습니다',
        error.response.status,
        data.error_code,
        data.details
      );
    }

    // 네트워크 에러 또는 타임아웃
    if (error.code === 'ECONNABORTED' || error.message === 'timeout of 30000ms exceeded') {
      return new ApiError(
        '요청 시간이 초과되었습니다',
        408,
        'timeout_error',
        undefined,
        true
      );
    }

    // 네트워크 연결 안 됨
    if (!error.response) {
      return new ApiError(
        '네트워크 연결을 확인해주세요',
        0,
        'network_error',
        undefined,
        true
      );
    }

    // 기타 에러
    return new ApiError(
      error.message,
      error.response?.status || 500,
      'unknown_error'
    );
  }

  // 예상하지 못한 에러
  if (error instanceof Error) {
    return new ApiError(error.message, 500, 'unexpected_error');
  }

  return new ApiError('예상하지 못한 오류가 발생했습니다', 500, 'unknown_error');
}

/**
 * React Query 에러 타입 가드
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * 에러 로깅 (개발 환경)
 */
export function logError(error: unknown, context: string = ''): void {
  const prefix = context ? `[${context}]` : '';
  if (isApiError(error)) {
    logger.error(prefix, {
      message: error.message,
      statusCode: error.statusCode,
      errorCode: error.errorCode,
      details: error.details,
    });
  } else if (error instanceof Error) {
    logger.error(prefix, error);
  } else {
    logger.error(prefix, 'Unknown error:', error);
  }
}
